import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useAIWorkspace } from "@/hooks/useAIWorkspace";
import { toast } from "sonner";
import {
  CommandDialog, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem,
} from "@/components/ui/command";
import {
  LayoutDashboard, Briefcase, KanbanSquare, ShieldCheck, Users, ScrollText,
  Settings, Compass, ClipboardList, Sparkles, Scale, UserRound, Bot, Pin, X, History,
} from "lucide-react";

const ACTIONS = {
  recruiter: [
    { label: "Go to Home", icon: LayoutDashboard, to: "/recruiter" },
    { label: "View all Jobs", icon: Briefcase, to: "/recruiter/jobs" },
    { label: "Open Candidates board", icon: KanbanSquare, to: "/recruiter/pipeline" },
    { label: "Interviews", icon: ClipboardList, to: "/recruiter/interviews" },
    { label: "Question bank", icon: ScrollText, to: "/recruiter/qbank" },
    { label: "Insights", icon: Scale, to: "/recruiter/analytics" },
  ],
  admin: [
    { label: "Governance overview", icon: ShieldCheck, to: "/admin" },
    { label: "Users & Roles", icon: Users, to: "/admin/users" },
    { label: "Audit & Rights requests", icon: ScrollText, to: "/admin/audit" },
    { label: "AI bindings & Retention", icon: Settings, to: "/admin/settings" },
  ],
  candidate: [
    { label: "Career home", icon: LayoutDashboard, to: "/candidate" },
    { label: "My applications", icon: ClipboardList, to: "/candidate/applications" },
    { label: "My profile & CV", icon: UserRound, to: "/candidate/profile" },
    { label: "Browse opportunities", icon: Compass, to: "/candidate/jobs" },
  ],
};

export function CommandBar({ open, setOpen }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [q, setQ] = useState("");

  const deriveContext = (path) => {
    let m = path.match(/\/recruiter\/(?:workspace|compare)\/([^/?]+)/);
    if (m) return { workspace_id: m[1] };
    m = path.match(/\/recruiter\/candidate\/([^/?]+)/);
    if (m) return { candidate_id: m[1] };
    return {};
  };
  const [busy, setBusy] = useState(false);
  const [pinned, setPinned] = useState([]);
  const [recents, setRecents] = useState([]);
  useEffect(() => {
    if (!open) return;
    try { const raw = localStorage.getItem(`hs_copilot_saved_${user?.id || "anon"}`); setPinned(raw ? JSON.parse(raw) : []); } catch { setPinned([]); }
    try { const raw = localStorage.getItem(`hs_copilot_recents_${user?.id || "anon"}`); setRecents(raw ? JSON.parse(raw) : []); } catch { setRecents([]); }
  }, [open, user?.id]);
  const actions = ACTIONS[user?.role] || [];
  const savedKey = `hs_copilot_saved_${user?.id || "anon"}`;
  const writePins = (next) => { setPinned(next); try { localStorage.setItem(savedKey, JSON.stringify(next.slice(0, 12))); } catch { /* ignore */ } };
  const pinPrompt = (text) => { const t = (text || "").trim(); if (!t || pinned.includes(t)) { toast.message("Already pinned"); return; } writePins([t, ...pinned]); toast.success("Prompt pinned"); };
  const unpinPrompt = (text) => writePins(pinned.filter((p) => p !== text));
  const run = (to) => { setOpen(false); nav(to); };

  const openChat = useAIWorkspace();
  const askAI = (override) => {
    const prompt = (typeof override === "string" ? override : q).trim();
    if (!prompt) return;
    setOpen(false); setQ("");
    const mode = user?.role === "candidate" ? "Career Copilot" : /\b(create|new|hire|draft a role)\b/i.test(prompt) ? "Create Job" : "Copilot";
    openChat(prompt, { mode, context: deriveContext(loc.pathname) });
  };

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput placeholder="Search actions or ask AI…" value={q} onValueChange={setQ} data-testid="command-input" />
      <CommandList>
        <CommandEmpty>Press Enter on “Ask AI” to run your request.</CommandEmpty>
        {q.trim() && (
          <CommandGroup heading="Ask AI">
            <CommandItem value={`__ai__ ${q}`} onSelect={askAI} data-testid="command-ask-ai">
              <Sparkles className="mr-2 h-4 w-4 text-primary" />
              {busy ? "Working…" : `Run: “${q.trim()}”`}
            </CommandItem>
            {user?.role !== "candidate" && !pinned.includes(q.trim()) && (
              <CommandItem value={`__pin__ ${q}`} onSelect={() => pinPrompt(q)} data-testid="command-pin-prompt">
                <Pin className="mr-2 h-4 w-4 text-muted-foreground" />
                Pin “{q.trim()}” for later
              </CommandItem>
            )}
          </CommandGroup>
        )}
        {user?.role !== "candidate" && pinned.length > 0 && (
          <CommandGroup heading="Pinned prompts">
            {pinned.filter((p) => !q.trim() || p.toLowerCase().includes(q.trim().toLowerCase())).map((p, i) => (
              <CommandItem key={`pin-${i}`} value={`pin-${p}`} onSelect={() => askAI(p)} data-testid={`command-pin-${i}`}>
                <Sparkles className="mr-2 h-4 w-4 text-primary" />
                <span className="truncate flex-1">{p}</span>
                <button onClick={(e) => { e.stopPropagation(); e.preventDefault(); unpinPrompt(p); }} data-testid={`command-unpin-${i}`} aria-label="Unpin prompt" className="ml-2 text-muted-foreground hover:text-destructive-foreground shrink-0">
                  <X className="h-3.5 w-3.5" />
                </button>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {user?.role !== "candidate" && recents.filter((p) => !pinned.includes(p) && (!q.trim() || p.toLowerCase().includes(q.trim().toLowerCase()))).length > 0 && (
          <CommandGroup heading="Recent prompts">
            {recents.filter((p) => !pinned.includes(p) && (!q.trim() || p.toLowerCase().includes(q.trim().toLowerCase()))).slice(0, 6).map((p, i) => (
              <CommandItem key={`recent-${i}`} value={`recent-${p}`} onSelect={() => askAI(p)} data-testid={`command-recent-${i}`}>
                <History className="mr-2 h-4 w-4 text-muted-foreground" />
                <span className="truncate flex-1">{p}</span>
                <button onClick={(e) => { e.stopPropagation(); e.preventDefault(); pinPrompt(p); }} data-testid={`command-recent-pin-${i}`} aria-label="Pin prompt" className="ml-2 text-muted-foreground hover:text-primary shrink-0">
                  <Pin className="h-3.5 w-3.5" />
                </button>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        <CommandGroup heading="Quick actions">
          {actions.map((a) => {
            const Icon = a.icon;
            return (
              <CommandItem key={a.label} onSelect={() => run(a.to)} data-testid={`command-${a.label.replace(/\s+/g, "-").toLowerCase()}`}>
                <Icon className="mr-2 h-4 w-4" />
                {a.label}
              </CommandItem>
            );
          })}
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
